import {LogLevel, PublicClientApplication, type BrowserAuthOptions, type Configuration} from '@azure/msal-browser';
import LoginProvider from 'Lib/LoginProvider';
import {LoginRequest} from 'Generated/RestClient.g';

const AZURE_USERNAME_KEY = 'azure-username';

export default class AzureActiveDirectoryLoginProvider implements LoginProvider {
    static readonly staticKey = 'azure-ad';
    readonly key = AzureActiveDirectoryLoginProvider.staticKey;

    private readonly msal: PublicClientApplication;

    private constructor(config: any) {
        const msalConfig: Configuration = {
            auth: {...config, navigateToLoginRequestUrl: false, onRedirectNavigate: (_: string) => false} as BrowserAuthOptions,
            cache: {
                cacheLocation: 'sessionStorage'
            },
            system: {
                loggerOptions: {
                    loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                        switch (level) {
                            case LogLevel.Error:
                                console.error(message);
                                return;
                            case LogLevel.Info:
                                console.info(message);
                                return;
                            case LogLevel.Verbose:
                                console.debug(message);
                                return;
                            case LogLevel.Warning:
                                console.warn(message);
                                return;
                        }
                    },
                    piiLoggingEnabled: false
                },
            },
        };
        this.msal = new PublicClientApplication(msalConfig);
    }

    public async silentLogin() {
        const username = localStorage.getItem(AZURE_USERNAME_KEY);
        if (!username) throw new Error('Username missing');

        const loginRequest = {
            scopes: ['openid', 'profile', 'User.Read'],
            loginHint: username,
        };

        const result = await this.msal.ssoSilent(loginRequest);
        if (result?.accessToken) {
            return new LoginRequest({azure: {token: result.accessToken}});
        }

        localStorage.removeItem(AZURE_USERNAME_KEY);
        throw new Error('Invalid login');
    }

    public async login(data: any, storeUserName: boolean = false) {
        const loginRequest = {
            scopes: ['openid', 'profile', 'User.Read'],
            forceRefresh: false,
        };

        let loginPopupResult = await this.msal.loginPopup(loginRequest);
        if (loginPopupResult?.accessToken) {
            if (storeUserName) localStorage.setItem(AZURE_USERNAME_KEY, loginPopupResult.account.username);
            return new LoginRequest({azure: {token: loginPopupResult.accessToken}});
        }

        throw new Error('No result received from login popup');
    }

    public async logout() {
        localStorage.removeItem(AZURE_USERNAME_KEY);
    }

    static async create(config: any): Promise<AzureActiveDirectoryLoginProvider> {
        const provider = new AzureActiveDirectoryLoginProvider(config);
        await provider.msal.initialize();
        return provider;
    }
}
